/**
 * Emits `item_opened` once per item actually opened.
 *
 * This is the signal that makes a review day visible at all. Every other reading signal is an
 * optional control somebody may never touch — on 2026-08-10 the reviewer worked 109 items and the
 * presence table recorded **nothing**, because opening an item to read it was not instrumented and
 * approving it is an audit row, not a reading one.
 *
 * **It fires from three places, and the third is the one that matters** (added 2026-08-12):
 * `MicroLessonReview` and `PracticeItemReview`, which mount at `/queue/lesson/:workItemId` and
 * `/queue/practice/:workItemId`; and the **work queue dashboard**, where the row is expanded in
 * place. A `fix` item never reaches either route — it is reviewed inline — so for five weeks this
 * signal was blind to 100% of the population it was built to measure, and the table held zero
 * `item_opened` rows while the reviewer decided 477 items.
 *
 * **The ref guard is not belt-and-braces — `StrictMode` is on** (`main.tsx:13`), which mounts,
 * unmounts and remounts every component in development, double-invoking effects. React preserves
 * refs across that remount, so keying the guard on the item id makes the second invocation a no-op
 * while a genuinely different item still emits. Without it every local session would report each
 * item opened twice, and since these samples widen the working-span figure, a doubled stream would
 * widen it with fabricated evidence.
 *
 * **The guard is CLEARED when the key goes absent**, which is what makes it a guard against
 * `StrictMode` rather than a once-per-session latch. On the dashboard the same row is opened,
 * collapsed and opened again all within one mounted component; a latch would record the first read
 * and silently discard every re-read, which is exactly the careful, slow reading this measurement
 * exists to see. `StrictMode`'s double invocation passes the *same* key twice with nothing in
 * between, so clearing on absence cannot re-open that hole.
 */

import { useEffect, useRef } from 'react';

import { recordActivitySignal } from './signals.js';

export function useItemOpenedSignal(itemKey: string | undefined): void {
  const emittedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!itemKey) {
      emittedFor.current = null;
      return;
    }
    if (emittedFor.current === itemKey) {
      return;
    }
    emittedFor.current = itemKey;
    // No step. The route is masked to its pattern by `recordActivitySignal` itself; the work item
    // travels as its own declared field, which is what lets a read be joined to the decision it led
    // to — see `ActivitySignal.workItemId` for why an id is permitted and prose is not.
    recordActivitySignal({ kind: 'item_opened', stepId: null, workItemId: itemKey });
  }, [itemKey]);
}
